/**
 * React Hook for User Behavior Tracking
 * 
 * Automatically tracks:
 * - Page views when component mounts
 * - Time spent on page (updates every 10 seconds)
 * - Page exits when component unmounts
 * 
 * Usage:
 *   const { trackPageView, trackPageExit } = useUserBehaviorTracking();
 *   trackPageView({ page_path: '/dashboard', page_name: 'Dashboard' });
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userBehaviorTrackingAPI } from '../services/userBehaviorTrackingApi';

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('user_behavior_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('user_behavior_session_id', sessionId);
  }
  return sessionId;
};

// Get page name from path
const getPageName = (path) => {
  const pageNames = {
    '/': 'Landing',
    '/login': 'Login',
    '/register': 'Register',
    '/dashboard': 'Dashboard',
    '/transactions': 'Transactions',
    '/accounts': 'Accounts',
    '/budgets': 'Budgets',
    '/subscriptions': 'Subscriptions',
    '/recurring-bills': 'Recurring Bills',
    '/categories': 'Categories',
    '/assets': 'Assets',
    '/asset-expenses': 'Asset Expenses',
    '/reconciliation': 'Reconciliation',
    '/tasks': 'Tasks',
    '/accounts-receivable': 'Accounts Receivable',
    '/accounts-payable': 'Accounts Payable',
    '/inventory': 'Inventory',
    '/settings': 'Settings',
    '/customers': 'Customers',
    '/vehicles': 'Vehicles',
    '/properties': 'Properties',
    '/creditors': 'Creditors',
    '/business': 'Business',
    '/staff': 'Staff',
    '/payroll': 'Payroll',
    '/reports': 'Reports',
    '/admin': 'Admin Dashboard',
    '/admin/users': 'Admin Users',
    '/support': 'Support'
  };
  return pageNames[path] || path.split('/').pop().replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

export const useUserBehaviorTracking = () => {
  const location = useLocation();
  const { isAuthenticated, token } = useAuth();
  const sessionId = useRef(getSessionId());
  const currentPagePath = useRef(null);
  const timeSpentInterval = useRef(null);
  const startTime = useRef(null);
  const previousPagePath = useRef(null);

  // Track page view
  const trackPageView = useCallback(async (pagePath, pageName = null) => {
    // Only track if user is authenticated
    if (!isAuthenticated || !token) {
      return;
    }

    try {
      const page_name = pageName || getPageName(pagePath);
      const user_agent = navigator.userAgent;
      const referrer = document.referrer || null;
      const screen_width = window.screen.width;
      const screen_height = window.screen.height;

      await userBehaviorTrackingAPI.trackPageView({
        session_id: sessionId.current,
        page_path: pagePath,
        page_name: page_name,
        previous_page_path: previousPagePath.current,
        user_agent,
        referrer,
        screen_width,
        screen_height
      });

      currentPagePath.current = pagePath;
      startTime.current = Date.now();
      previousPagePath.current = pagePath;

      // Start tracking time spent (update every 10 seconds)
      if (timeSpentInterval.current) {
        clearInterval(timeSpentInterval.current);
      }

      timeSpentInterval.current = setInterval(async () => {
        // Only track if still authenticated
        if (!isAuthenticated || !token || !currentPagePath.current || !startTime.current) {
          if (timeSpentInterval.current) {
            clearInterval(timeSpentInterval.current);
            timeSpentInterval.current = null;
          }
          return;
        }

        const timeSpent = Math.floor((Date.now() - startTime.current) / 1000);
        try {
          await userBehaviorTrackingAPI.updateTimeSpent({
            session_id: sessionId.current,
            page_path: currentPagePath.current,
            time_spent_seconds: timeSpent
          });
        } catch (error) {
          // Silently fail - don't interrupt user experience
          if (process.env.NODE_ENV === 'development') {
            const status = error.response?.status;
            if (status !== 401 && status !== 404 && status !== 503) {
              console.debug('Failed to update time spent:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
            }
          }
        }
      }, 10000); // Update every 10 seconds
    } catch (error) {
      // Silently fail - don't interrupt user experience
      console.debug('Failed to track page view:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
  }, []);

  // Track page exit
  const trackPageExit = useCallback(async (nextPagePath = null) => {
    // Only track if user is authenticated
    if (!isAuthenticated || !token || !currentPagePath.current) {
      return;
    }

    try {
      // Clear interval
      if (timeSpentInterval.current) {
        clearInterval(timeSpentInterval.current);
        timeSpentInterval.current = null;
      }

      // Calculate final time spent
      const timeSpent = startTime.current 
        ? Math.floor((Date.now() - startTime.current) / 1000)
        : 0;

      await userBehaviorTrackingAPI.trackPageExit({
        session_id: sessionId.current,
        page_path: currentPagePath.current,
        next_page_path: nextPagePath
      });
    } catch (error) {
      // Silently fail - don't interrupt user experience
      if (process.env.NODE_ENV === 'development') {
        const status = error.response?.status;
        if (status !== 401 && status !== 404 && status !== 503) {
          console.debug('Failed to track page exit:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
        }
      }
    }
  }, [isAuthenticated, token]);

  // Track page changes automatically (only when authenticated)
  useEffect(() => {
    // Don't track if not authenticated
    if (!isAuthenticated || !token) {
      // Clear any existing intervals
      if (timeSpentInterval.current) {
        clearInterval(timeSpentInterval.current);
        timeSpentInterval.current = null;
      }
      return;
    }

    const currentPath = location.pathname;
    
    // Track exit of previous page
    if (currentPagePath.current && currentPagePath.current !== currentPath) {
      trackPageExit(currentPath);
    }

    // Track new page view
    trackPageView(currentPath);

    // Cleanup on unmount
    return () => {
      if (timeSpentInterval.current) {
        clearInterval(timeSpentInterval.current);
      }
      trackPageExit();
    };
  }, [location.pathname, isAuthenticated, token, trackPageView, trackPageExit]);

  return {
    sessionId: sessionId.current,
    trackPageView,
    trackPageExit
  };
};

export default useUserBehaviorTracking;

